import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type SortDirection = 'asc' | 'desc' | null;

interface SortableTableHeaderProps {
  label: string;
  sortKey: string;
  currentSortKey: string | null;
  currentSortDirection: SortDirection;
  onSort: (key: string, direction: SortDirection) => void;
  className?: string;
}

/**
 * Sortable Table Header Component
 * Provides clickable column headers with visual indicators for sort direction
 */
export const SortableTableHeader: React.FC<SortableTableHeaderProps> = ({
  label,
  sortKey,
  currentSortKey,
  currentSortDirection,
  onSort,
  className,
}) => {
  const isActive = currentSortKey === sortKey;

  const handleClick = () => {
    let newDirection: SortDirection = 'asc';

    if (isActive) {
      if (currentSortDirection === 'asc') {
        newDirection = 'desc';
      } else if (currentSortDirection === 'desc') {
        newDirection = null;
      }
    }

    onSort(sortKey, newDirection);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={cn(
        'h-auto p-0 font-medium hover:bg-transparent hover:text-foreground',
        isActive && 'text-foreground',
        !isActive && 'text-muted-foreground',
        className
      )}
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
        <div className="w-4 h-4">
          {!isActive && <ArrowUpDown className="w-4 h-4 opacity-50" />}
          {isActive && currentSortDirection === 'asc' && (
            <ArrowUp className="w-4 h-4" />
          )}
          {isActive && currentSortDirection === 'desc' && (
            <ArrowDown className="w-4 h-4" />
          )}
        </div>
      </div>
    </Button>
  );
};

/**
 * Utility function to sort an array of objects
 */
export const sortData = <T extends Record<string, any>>(
  data: T[],
  sortKey: string | null,
  direction: SortDirection
): T[] => {
  if (!sortKey || !direction) {
    return data;
  }

  const sorted = [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return direction === 'asc' ? 1 : -1;
    if (bValue == null) return direction === 'asc' ? -1 : 1;

    // Handle string comparison (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      return direction === 'asc' ? comparison : -comparison;
    }

    // Handle number comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // Handle date comparison
    if (aValue instanceof Date && bValue instanceof Date) {
      return direction === 'asc'
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime();
    }

    // Fallback to string comparison
    const aStr = String(aValue);
    const bStr = String(bValue);
    const comparison = aStr.localeCompare(bStr);
    return direction === 'asc' ? comparison : -comparison;
  });

  return sorted;
};

export default SortableTableHeader;
